import React, { useEffect, useState, useCallback } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import PartySelectSection from '../../../../documents/components/PartySelectSection'
import { request } from '../../../../../../utils/request';
import { useForm } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import { Box, ToggleButton, ToggleButtonGroup, Card, Typography, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import InvoiceDatesSection from '../../../../documents/components/InvoiceDatesSection';
import NotesTermsSection from '../../../../documents/components/NotesTermsSection';
import AmountSummarySection from '../../../../documents/components/AmountSummarySection';
import ItemsTableSection from '../../../../documents/components/ItemsTableSection';
import { GstType, QuotationFormData, TaxBreakdownItem } from '../../../../types';
import { useGstMode } from '../../../../../../utils/useGstMode';
import { toast } from 'react-toastify';
import { useFinancialYear } from '../../../../../../utils/useFinancialYear';
import { useSearchParams } from 'react-router-dom';

const CreateQuotation = () => {
  const [searchParams] = useSearchParams();
  const financialYear = useFinancialYear();
  const defaultQuotationNumber = `QUO${financialYear}-1`
  const navigate = useNavigate();
  const { gstEnabled } = useGstMode();
  const { id } = useParams();
  const isEditMode = !!id;

  const [taxableAmount, setTaxableAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [, setTotalTax] = useState(0);
  const [, setTotalDiscount] = useState(0);
  const [, setItems] = useState<any[]>([]);
  const [taxBreakdown, setTaxBreakdown] = useState<TaxBreakdownItem[]>([]);
  const [nextQuotationNumber, setNextQuotationNumber] = useState(defaultQuotationNumber);
  const [isLoadingQuotationNumber, setIsLoadingQuotationNumber] = useState(false);
  const [loading, setLoading] = useState(false);

  const queryGstType = searchParams.get('gstType');

  // Determine default GST type based on query parameter or gstEnabled
  const getDefaultGstType = (): GstType => {
    if (queryGstType === 'NON-GST') {
      return 'NON-GST';
    }
    return gstEnabled ? 'GST' : 'GST';
  };

  const methods = useForm<QuotationFormData>({
    defaultValues: {
      quotationType: 'QUOTATION',
      gstType: getDefaultGstType(), // Use the function to get default
      party: '',
      quotationNumber: '',
      quotationDate: new Date().toISOString().split('T')[0],
      date: new Date().toISOString().split('T')[0],
      dueDate: '',
      paymentTerms: '',
      items: [],
      charges: [],
      discount: undefined,
      notes: '',
      terms: '',
      roundOff: false,
      totalAmount: 0,
      taxableAmount: 0,
      subtotal: 0,
      // receivedAmount: 0,
      // balanceAmount: 0,
      taxBreakdown: [],
    },
    mode: 'onChange'
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    getValues,
    reset
  } = methods;

  // Watch gstType value
  const gstType = watch('gstType', getDefaultGstType());
  const quotationType = watch('quotationType', 'QUOTATION');

  // Function to fetch next quotation number from API
  const fetchNextQuotationNumber = useCallback(async () => {
    if (isEditMode) return; // Don't fetch for edit mode

    setIsLoadingQuotationNumber(true);
    try {
      const res = await request({
        url: 'quotation/get-next-quotation-number',
        method: "get",
        params: { quotationType: quotationType, gstType: gstType }
      });

      if (res.success && res.quotationNumber) {
        const newQuotationNumber = res.quotationNumber;
        setNextQuotationNumber(newQuotationNumber);
        setValue('quotationNumber', newQuotationNumber);
      }
    } catch (error) {
      console.error("Error fetching next quotation number:", error);
      // Fallback: Use default
      setNextQuotationNumber(`QUO${financialYear}-1`);
      setValue('quotationNumber', `QUO${financialYear}-1`);
    } finally {
      setIsLoadingQuotationNumber(false);
    }
  }, [financialYear, gstType, quotationType, isEditMode, setValue]);

  // Function to fetch existing quoataion data for edit mode
  const fetchQuotationData = useCallback(async () => {
    if (!isEditMode) return;

    setLoading(true);
    try {
      const res = await request({
        url: `quotation/get-single-quotation/${id}`,
        method: "get"
      });

      if (res.success && res.data) {
        const quotationData = res.data;

        // Format dates to YYYY-MM-DD
        const formatDate = (dateString: string | Date) => {
          if (!dateString) return new Date().toISOString().split('T')[0];
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        };

        // Get party ID - handle both object and string
        let partyId = '';
        if (typeof quotationData.party === 'object' && quotationData.party !== null) {
          partyId = quotationData.party._id || '';
        } else if (typeof quotationData.party === 'string') {
          partyId = quotationData.party;
        }

        // Prepare form data
        const formData = {
          quotationType: quotationData.quotationType || 'QUOTATION',
          gstType: quotationData.gstType || 'GST',
          party: partyId,
          quotationNumber: quotationData.quotationNumber || '',
          quotationDate: formatDate(quotationData.quotationDate),
          date: formatDate(quotationData.date),
          dueDate: formatDate(quotationData.dueDate),
          paymentTerms: quotationData.paymentTerms || '',
          items: quotationData.items || [],
          charges: quotationData.charges || [],
          discount: quotationData.discount || undefined,
          notes: quotationData.notes || '',
          terms: quotationData.terms || '',
          roundOff: quotationData.roundOff || false,
          totalAmount: quotationData.totalAmount || 0,
          taxableAmount: quotationData.taxableAmount || 0,
          subtotal: quotationData.subtotal || 0,
          receivedAmount: quotationData.receivedAmount || 0,
          balanceAmount: quotationData.balanceAmount || 0,
          taxBreakdown: quotationData.taxBreakdown || [],
        };

        reset(formData);

        // Update state values
        setTaxableAmount(quotationData.taxableAmount || 0);
        setTotalAmount(quotationData.totalAmount || 0);
        setTotalTax(0);
        setTotalDiscount(0);
        setItems(quotationData.items || []);
        setTaxBreakdown(quotationData.taxBreakdown || []);

      } else {
        toast.error(res.message || "Failed to load quotation data");
      }
    } catch (error: any) {
      console.error("Error fetching quotation data:", error);
      toast.error(error?.message || "Failed to load quotation data");
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode, reset]);

  // Function to update all items tax to "none" when NON-GST is selected
  const updateItemsTaxToNone = useCallback(() => {
    const currentItems = getValues('items') || [];
    if (currentItems.length === 0) return;

    const updatedItems = currentItems.map((item: any) => ({
      ...item,
      tax: {
        type: 'none',
        rate: 0
      },
      amount: ((item.quantity || 1) * (item.price || 0)) - (item.discount?.amount || 0)
    }));

    setItems(updatedItems);
    setValue('items', updatedItems);
    setTaxBreakdown([]);
    setValue('taxBreakdown', []);
  }, [getValues, setValue]);

  // Effect to handle query parameter for GST type (only for create mode)
  useEffect(() => {
    if (!isEditMode && queryGstType === 'NON-GST') {
      setValue('gstType', 'NON-GST');
      // Update items tax to none for NON-GST
      updateItemsTaxToNone();
    }
  }, [isEditMode, queryGstType, setValue, updateItemsTaxToNone]);

  // Initial data fetch
  useEffect(() => {
    if (isEditMode) {
      fetchQuotationData();
    } else {
      fetchNextQuotationNumber();
    }
  }, [isEditMode, fetchQuotationData, fetchNextQuotationNumber]);

  // Update next quotation number when quotation type changes (only for create mode)
  useEffect(() => {
    if (!isEditMode) {
      fetchNextQuotationNumber();
    }
  }, [quotationType, isEditMode, fetchNextQuotationNumber]);

  const requestWrapper = ({ url, method, params }: { url: string; method: string; params?: any }) => {
    return request({ method: method as any, url, params });
  }

  // Handle GST type change
  const handleGstTypeChange = (event: React.MouseEvent<HTMLElement>, newGstType: GstType | null) => {
    if (newGstType !== null) {
      setValue('gstType', newGstType);
      if (newGstType === 'NON-GST') {
        updateItemsTaxToNone();
      }
    }
  };

  // Effect to handle GST type changes
  useEffect(() => {
    if (gstType === 'NON-GST') {
      updateItemsTaxToNone();
    }
  }, [gstType, updateItemsTaxToNone]);

  const onSubmit = async (data: QuotationFormData) => {

    try {
      // Prepare the payload
      const payload = {
        ...data,
        party: data.party,
        quotationNumber: isEditMode ? data.quotationNumber : (data.quotationNumber || undefined),
      };
      
      const apiUrl = isEditMode
        ? `quotation/update-quotation/${id}`
        : 'quotation/create-quotation';
      const method = isEditMode ? 'put' : 'post';

      const res = await request({
        url: apiUrl,
        method: method,
        data: payload
      });

      if (res.success) {
        const successMessage = isEditMode
          ? "quotation Updated Successfully"
          : "quotation Created Successfully";
        toast.success(res.message || successMessage);

        // Navigate to quotation list
        navigate('/accounts/sales/quotation');
      } else {
        toast.error(res.message || "Operation failed");
      }
    } catch (error: any) {
      console.error("Error submitting quotation:", error);
      toast.error(error || 'Something went wrong');
    }
  }

  const handleCancel = () => {
    navigate('/accounts/sales/quotation');
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography>Loading Quotation data...</Typography>
      </Box>
    );
  }

  return (
    <>
      <DocumentHeader
        heading={isEditMode ? 'Edit Quotation' : 'Create Quotation'}
        showBackButton
        showCancelButton={true}
        showSaveButton={true}
        onCancel={handleCancel}
        onSave={handleSubmit(onSubmit)}
        saveButtonText={isEditMode ? 'Update Quotation' : 'Create Quotation'}
        saveDisabled={isSubmitting}
      />

      {/* GST Type Selection Section */}
      {!gstEnabled && (
        <Card sx={{
          mb: 2,
          p: 2,
          borderRadius: 1,
          boxShadow: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" fontWeight="medium">
              Quotation Type:
            </Typography>

            <ToggleButtonGroup
              value={gstType}
              exclusive
              onChange={handleGstTypeChange}
              aria-label="GST Type"
              size="small"
              color="primary"
              disabled={isEditMode}
            >
              <ToggleButton value="GST" aria-label="GST">
                GST Quotation
              </ToggleButton>
              <ToggleButton value="NON-GST" aria-label="NON-GST">
                Non-GST Quotation
              </ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="caption" color={gstType === 'GST' ? "success.main" : "info.main"}>
              {gstType === 'GST'
                ? 'Includes GST tax calculations'
                : 'No GST tax calculations'}
            </Typography>
          </Box>
          {isEditMode && (
            <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
              GST type cannot be changed for existing Quotation.
            </Alert>
          )}
        </Card>
      )}

      {/* Form Provider */}
      <FormProvider {...methods}>
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={'flex'} gap={1} sx={{ alignItems: 'stretch' }}>
            <Box sx={{ flex: 1, height: 'auto' }}>
              <PartySelectSection
                request={requestWrapper}
                apiParams={{ status: 'active', limit: 50 }}
                billToLabel="Bill To"
                formName="party"
                onAddNewParty={() => navigate('/parties/addParty')}
                compact={true}
                sendPartyIdOnly={true}
              />

              {errors.party && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  Please select a party
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <InvoiceDatesSection
                invoiceNumberLabel="Quotation No"
                invoiceDateLabel="Quotation Date"
                paymentTermsLabel="Valid For"
                dueDateLabel="Validity Date"
                formFieldNames={{
                  invoiceNumber: 'quotationNumber',
                  invoiceDate: 'quotationDate',
                  paymentTerms: 'paymentTerms',
                  dueDate: 'dueDate'
                }}
                autoGenerateInvoiceNumber={!isEditMode}
                nextInvoiceNumber={nextQuotationNumber}
                showPaymentTerms={true}
                showDueDate={true}
                compact={true}
                disabled={isLoadingQuotationNumber || isEditMode}
                onInvoiceDateChange={() => { }}
                onPaymentTermsChange={() => { }}
                onDueDateChange={() => { }}
                paymentTermsOptions={[
                  { value: 'immediate', label: 'Immediate', days: 0 },
                  { value: 'net7', label: 'Net 7 Days', days: 7 },
                  { value: 'net15', label: 'Net 15 Days', days: 15 },
                  { value: 'net30', label: 'Net 30 Days', days: 30 },
                  { value: 'net45', label: 'Net 45 Days', days: 45 },
                  { value: 'net60', label: 'Net 60 Days', days: 60 },
                ]}
              />
              {isLoadingQuotationNumber && !isEditMode && (
                <Typography variant="caption" color="text.secondary">
                  Loading next quotation number...
                </Typography>
              )}
              {nextQuotationNumber && !isEditMode && (
                <Typography variant="caption" color="primary">
                  Next available number: {nextQuotationNumber}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Items Table Section */}
          <Box>
            <ItemsTableSection
              formFieldName="items"
              onItemsChange={(updatedItems) => {
                setItems(updatedItems);
                setValue('items', updatedItems);
              }}
              onSubtotalChange={() => { }}
              onTotalDiscountChange={(discount) => {
                setTotalDiscount(discount);
              }}
              onTotalTaxChange={(tax) => {
                setTotalTax(tax);
              }}
              onTaxableAmountChange={(taxable) => {
                setTaxableAmount(taxable);
                setValue('taxableAmount', taxable);
              }}
              onTotalAmountChange={(total) => {
                setTotalAmount(total);
                setValue('totalAmount', total);
              }}
              onTaxBreakdownChange={(breakdown) => {
                if (gstType === 'GST') {
                  setTaxBreakdown(breakdown);
                  setValue('taxBreakdown', breakdown);
                }
              }}
              showHsnNo={gstType === 'GST'}
              showDiscount={true}
              showTax={gstType === 'GST'}
              disabled={false}
              compact={true}
              maxItems={100}
            />
          </Box>

          <Box display={'flex'} gap={1} sx={{ alignItems: "stretch" }}>
            <Box sx={{ flex: 1 }}>
              <NotesTermsSection
                notesLabel="Additional Notes"
                termsLabel="Terms & Conditions"
                addNoteButtonText="Add Note"
                addTermsButtonText="Add Terms"
                formFieldNames={{
                  notes: 'notes',
                  terms: 'terms'
                }}
                showNotes={true}
                showTerms={true}
                allowMultipleNotes={false}
                allowMultipleTerms={false}
                compact={true}
                maxNotesLength={500}
                maxTermsLength={1000}
                onNotesChange={() => { }}
                onTermsChange={() => { }}
                notesPlaceholder="Add any special instructions, delivery notes, or additional information..."
                termsPlaceholder="Add payment terms, return policies, warranties, or other conditions..."
                defaultExpanded={!!getValues('notes') || !!getValues('terms')}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <AmountSummarySection
                taxableAmount={taxableAmount}
                totalAmount={totalAmount}
                taxBreakdown={gstType === 'GST' ? taxBreakdown : []}
                formFieldNames={{
                  charges: 'charges',
                  discount: 'discount',
                  roundOff: 'roundOff',
                  totalAmount: 'totalAmount',
                  taxableAmount: 'taxableAmount',
                  subtotal: 'subtotal',
                  taxBreakdown: 'taxBreakdown',
                }}
                compact={true}
                showBalanceAmount={false}
                showReceivedAmount={false}
                taxRate={gstType === 'GST' ? 18 : 0}
                showTaxableAmount={gstType === 'GST'}
                onTotalAmountChange={() => { }}
              />
            </Box>
          </Box>
        </Box>
      </FormProvider>
    </>
  )
}

export default CreateQuotation;